import React, { useState, useEffect } from 'react';
import { Clock, Check, X, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OvertimeRequestsManager = ({ locationId, sessionId }) => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadRequests();
    const interval = setInterval(loadRequests, 10000);
    return () => clearInterval(interval);
  }, [locationId]);

  const loadRequests = async () => {
    try {
      const response = await axios.get(
        `${API}/manager/overtime-requests/${locationId}`,
        { params: { session_id: sessionId } }
      );
      if (response.data.success) {
        setRequests(response.data.requests);
      }
    } catch (error) {
      console.error('Error loading overtime requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReview = async (requestId, status) => {
    setProcessingId(requestId);
    try {
      const response = await axios.put(
        `${API}/manager/overtime-request/${requestId}/review`,
        { status, reviewed_by: 'Manager' },
        { params: { session_id: sessionId } }
      );
      if (response.data.success) {
        toast.success(response.data.message || `Request ${status}`);
        loadRequests();
      }
    } catch (error) {
      console.error('Error reviewing request:', error);
      toast.error(`Failed to ${status} request`);
    } finally {
      setProcessingId(null);
    }
  };

  const calculateEarlyMinutes = (requestedTime, scheduledStart) => {
    if (!requestedTime || !scheduledStart) return 0;
    const [reqH, reqM] = requestedTime.split(':').map(Number);
    const [schH, schM] = scheduledStart.split(':').map(Number);
    return Math.max(0, (schH * 60 + schM) - (reqH * 60 + reqM));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-500">No pending early arrival requests</p>
      </div>
    );
  }

  const pendingApprovalRequests = requests.filter(r => r.status === 'pending');
  const unauthorisedRequests = requests.filter(r => r.status === 'unauthorised');

  return (
    <div className="space-y-6">
      {/* Pending Approval Requests */}
      {pendingApprovalRequests.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-violet-400" />
            Early Arrival Requests (Awaiting Approval)
          </h3>
          <div className="space-y-4">
            {pendingApprovalRequests.map((request) => {
              const earlyMinutes = calculateEarlyMinutes(request.requested_checkin_time, request.scheduled_start);
              return (
                <div
                  key={request.id}
                  data-testid={`overtime-request-${request.id}`}
                  className="bg-violet-500/5 border border-violet-500/15 rounded-xl p-4 transition-colors hover:bg-violet-500/10"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-violet-400" />
                        <h4 className="font-semibold text-white">{request.employee_name}</h4>
                        <span className="text-xs bg-violet-500/20 text-violet-300 px-2 py-0.5 rounded-full ring-1 ring-violet-400/20">
                          {earlyMinutes} min early
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-zinc-400">
                        <p>Wants to check in at: <span className="font-medium text-white">{request.requested_checkin_time}</span></p>
                        <p>Scheduled start: <span className="font-medium text-white">{request.scheduled_start}</span></p>
                        <p>Date: <span className="font-medium text-white">{new Date(request.date).toLocaleDateString()}</span></p>
                        <p className="text-xs text-zinc-500 mt-2">
                          Requested {new Date(request.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleReview(request.id, 'approved')}
                        disabled={processingId === request.id}
                        data-testid={`approve-overtime-${request.id}`}
                        className="bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-50 text-emerald-200 ring-1 ring-emerald-400/25 px-4 py-2 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium"
                      >
                        {processingId === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Approve
                      </button>
                      <button
                        onClick={() => handleReview(request.id, 'declined')}
                        disabled={processingId === request.id}
                        data-testid={`decline-overtime-${request.id}`}
                        className="bg-rose-500/15 hover:bg-rose-500/25 disabled:opacity-50 text-rose-200 ring-1 ring-rose-400/25 px-4 py-2 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium"
                      >
                        <X className="w-4 h-4" />
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unauthorised Early Arrivals */}
      {unauthorisedRequests.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Unauthorised Early Arrivals (Started Without Approval)
          </h3>
          <p className="text-sm text-zinc-500 mb-4">
            These employees started early without prior approval. Approve to authorise their hours, or decline to keep them as unauthorised.
          </p>
          <div className="space-y-4">
            {unauthorisedRequests.map((request) => {
              const earlyMinutes = calculateEarlyMinutes(request.requested_checkin_time, request.scheduled_start);
              return (
                <div
                  key={request.id}
                  data-testid={`unauthorised-overtime-${request.id}`}
                  className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 transition-colors hover:bg-amber-500/10"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <h4 className="font-semibold text-white">{request.employee_name}</h4>
                        <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full ring-1 ring-amber-400/20">
                          {earlyMinutes} min early - Unauthorised
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-zinc-400">
                        <p>Started at: <span className="font-medium text-white">{request.requested_checkin_time}</span></p>
                        <p>Scheduled start: <span className="font-medium text-white">{request.scheduled_start}</span></p>
                        <p>Date: <span className="font-medium text-white">{new Date(request.date).toLocaleDateString()}</span></p>
                        <p className="text-xs text-amber-400/70 mt-2 font-medium">
                          Employee started without approval - hours currently unauthorised
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleReview(request.id, 'approved')}
                        disabled={processingId === request.id}
                        data-testid={`authorise-overtime-${request.id}`}
                        className="bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-50 text-emerald-200 ring-1 ring-emerald-400/25 px-4 py-2 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium"
                      >
                        {processingId === request.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Authorise
                      </button>
                      <button
                        onClick={() => handleReview(request.id, 'declined')}
                        disabled={processingId === request.id}
                        data-testid={`decline-unauthorised-${request.id}`}
                        className="bg-rose-500/15 hover:bg-rose-500/25 disabled:opacity-50 text-rose-200 ring-1 ring-rose-400/25 px-4 py-2 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium"
                      >
                        <X className="w-4 h-4" />
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OvertimeRequestsManager;
